import { TrackEvent } from '../../../apps/professional-app/fa/common/tracking'
import {
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDecompositionOutcome,
  CrossClientQueryDecompositionOutcomes,
  type CrossClientQuerySuggestedDecomposition,
} from '../../../models/CrossClient'
import { isRecognisedOutcome, unusableReason } from './decompositionGate'
import { type CrossClientReviewState } from './hooks'

export const DecompositionExitPoints = {
  REJECTION_CARD: 'rejection_card',
  SUGGESTION_CARD: 'suggestion_card',
  PREVIEW: 'preview',
  RESULTS: 'results',
  RECOMMENDED_TAB: 'recommended_tab',
  // Cancelled while the question was still being decomposed, before any card was shown.
  DECOMPOSING: 'decomposing',
} as const
export type DecompositionExitPoint = (typeof DecompositionExitPoints)[keyof typeof DecompositionExitPoints]

export interface DecompositionContext {
  outcome: CrossClientQueryDecompositionOutcome | null
  hadSuggestion: boolean
}

export const exitEventDetails = (context: DecompositionContext | null, exitFrom: DecompositionExitPoint | null) => ({
  outcome: context?.outcome ?? null,
  hadSuggestion: context?.hadSuggestion ?? false,
  exitFrom,
})

export interface QueryConfirmedEventInput {
  type: 'custom' | 'pre-defined'
  query: string
  sharedMode: CrossClientQueryClientQueryMode
  context: DecompositionContext | null
}

export const queryConfirmedEvent = ({
  type,
  query,
  sharedMode,
  context,
}: QueryConfirmedEventInput): { eventName: TrackEvent; eventDetails: Record<string, unknown> } => ({
  eventName: TrackEvent.X_CLIENT_QUERY_CONFIRMED,
  eventDetails: { type, query, sharedMode, ...exitEventDetails(context, DecompositionExitPoints.RESULTS) },
})

export interface DecompositionEventInput {
  outcome?: CrossClientQueryDecompositionOutcome | null
  suggestion?: CrossClientQuerySuggestedDecomposition
  criteriaCount: number
  rejectionReason?: string | null
  type: 'custom' | 'pre-defined'
  query: string
}

export const decompositionEvent = ({
  outcome,
  suggestion,
  criteriaCount,
  rejectionReason,
  type,
  query,
}: DecompositionEventInput): { eventName: TrackEvent; eventDetails: Record<string, unknown> } => {
  const eventDetails = {
    outcome: outcome ?? null,
    hasSuggestion: !!suggestion,
    criteriaCount,
    suggestedCriteriaCount: suggestion ? (suggestion.decomposition.atomicClauses?.length ?? 0) : null,
    unusableReason: unusableReason({ outcome, rejectionReason, clauseCount: criteriaCount }),
    hasRejectionReason: !!rejectionReason?.trim(),
    type,
    query,
    suggestedQuestion: suggestion?.userQuestion ?? null,
  }

  if (!isRecognisedOutcome(outcome)) {
    return { eventName: TrackEvent.X_CLIENT_QUESTION_OUTCOME_UNRECOGNISED, eventDetails }
  }
  if (outcome === CrossClientQueryDecompositionOutcomes.UNANSWERABLE) {
    return { eventName: suggestion ? TrackEvent.X_CLIENT_QUESTION_CORRECTED : TrackEvent.X_CLIENT_QUESTION_REJECTED, eventDetails }
  }
  if (outcome === CrossClientQueryDecompositionOutcomes.UNDERSPECIFIED) {
    return { eventName: suggestion ? TrackEvent.X_CLIENT_QUESTION_REFINED : TrackEvent.X_CLIENT_QUESTION_REFINEMENT_MISSING, eventDetails }
  }
  return { eventName: TrackEvent.X_CLIENT_QUESTION_SPECIFIED, eventDetails }
}

export const suggestionCardType = (reviewState: CrossClientReviewState | null): 'refined' | 'corrected' | null => {
  if (reviewState?.kind === 'refinement') {
    return 'refined'
  }
  if (reviewState?.kind === 'rejected' && !!reviewState.suggestion) {
    return 'corrected'
  }
  return null
}

export const dismissEventName = (reviewState: CrossClientReviewState | null): TrackEvent | null => {
  if (suggestionCardType(reviewState) !== null) {
    return TrackEvent.X_CLIENT_SUGGESTION_ABANDONED
  }
  if (reviewState?.kind === 'rejected') {
    return TrackEvent.X_CLIENT_REJECTION_DISMISSED
  }
  if (reviewState?.kind === 'confirm') {
    return TrackEvent.X_CLIENT_QUERY_PREVIEW_CANCELLED
  }
  return null
}

export const exitPointForReviewState = (reviewState: CrossClientReviewState | null): DecompositionExitPoint | null => {
  if (suggestionCardType(reviewState) !== null) {
    return DecompositionExitPoints.SUGGESTION_CARD
  }
  if (reviewState?.kind === 'rejected') {
    return DecompositionExitPoints.REJECTION_CARD
  }
  // The confirm card took the preview card's place; keeping its exit point keeps the reports continuous.
  if (reviewState?.kind === 'confirm') {
    return DecompositionExitPoints.PREVIEW
  }
  return null
}
